import { InputAdornment } from '@mui/material';
import type { GridColDef } from '@mui/x-data-grid';
import { createPlanPageSelectors, getSelectorAttribute } from '@vy/app-selectors';
import { getCurrencySymbol } from '@vy/services';
import type { Currency, FixedProductInput, PricingModel } from '@vy/types';
import { DEFAULT_CURRENCY, PRICING_MODELS } from '@vy/types';
import { isNotNullOrUndefined, isNullOrUndefined } from '@vy/utils';
import type { FieldPath } from 'react-hook-form';
import { FIXED_CHARGES_EDIT_COLUMNS_KEYS } from '../../FixedChargesEditTable/fixed-charges-edit-table-consts';
import { PromotionChargeAddonsCell } from '../promotion-charges-edit-table-components/PromotionChargeAddonsCell';
import { PromotionEditCell } from '../promotion-charges-edit-table-components/PromotionEditCell';
import { RemovePromotionRow } from '../promotion-charges-edit-table-components/RemovePromotionRow';

export const PROMOTIONS_CHARGES_EDIT_COLUMNS_KEYS = Object({
  TYPE: 'pricingModel',
  NAME: 'name',
  AMOUNT: 'customAmount',
  FEE: 'fee',
} satisfies Record<
  string,
  FieldPath<FixedProductInput> | 'customAmount' | 'fee' | 'deleteRow' | 'pricingModel'
>);

export const PROMOTIONS_CHARGES_EDIT_COLUMNS: GridColDef<FixedProductInput>[] = [
  {
    field: PROMOTIONS_CHARGES_EDIT_COLUMNS_KEYS.TYPE,
    headerName: 'Type',
    sortable: false,
    minWidth: 180,
    valueGetter: (_value, row) => getPromotionTypeLabel(row.variants[0].pricing.pricingModel),
  },
  {
    field: PROMOTIONS_CHARGES_EDIT_COLUMNS_KEYS.NAME,
    headerName: 'Name',
    flex: 1,
    sortable: false,
    renderCell: ({ row, field }) => (
      <PromotionEditCell
        promotion={row}
        field={field as FieldPath<FixedProductInput>}
        placeholder="Promotion name"
        tooltipPlacement="top-start"
        fullWidth
      />
    ),
  },
  {
    field: PROMOTIONS_CHARGES_EDIT_COLUMNS_KEYS.AMOUNT,
    headerName: 'Amount',
    flex: 1,
    sortable: false,
    renderCell: ({ row }) => {
      const fieldPath = getFieldPath(row.variants[0].pricing.pricingModel);
      const prefix = getAmountPrefix(row.variants[0].pricing.pricingModel, DEFAULT_CURRENCY);

      if (isNullOrUndefined(fieldPath)) {
        return null;
      }

      return (
        <PromotionEditCell
                      {...getSelectorAttribute(createPlanPageSelectors.products.fixed.amountInput)}
          promotion={row}
          field={fieldPath}
          valueAsNumber
          type="number"
          placeholder="0"
          tooltipPlacement="top-start"
          slotProps={{
            input: {
              startAdornment: isNotNullOrUndefined(prefix) ? (
                <InputAdornment
                  position="start"
                  sx={{
                    paddingBottom: '2px',
                  }}
                >
                  {prefix}
                </InputAdornment>
              ) : undefined,
            },
          }}
        />
      );
    },
  },
  {
    field: FIXED_CHARGES_EDIT_COLUMNS_KEYS.OTHER,
    headerName: 'Add on',
    headerClassName: 'pr-4',
    headerAlign: 'right',
    cellClassName: 'justify-end items-center',
    sortable: false,
    flex: 2,
    renderCell: ({ row }) => <PromotionChargeAddonsCell promotion={row} />,
  },
  {
    field: 'deleteRow',
    headerName: '',
    cellClassName: 'justify-center',
    width: 30,
    renderCell: ({ row }) => (
      <RemovePromotionRow
                      {...getSelectorAttribute(createPlanPageSelectors.products.fixed.removeRowButton)}
        promotion={row}
      />
    ),
  },
];

function getPromotionTypeLabel(promotionType: PricingModel | undefined) {
  if (promotionType === PRICING_MODELS.FixedDeduction) {
    return 'Fixed';
  }

  if (promotionType === PRICING_MODELS.PercentageDeduction) {
    return 'Percentage';
  }

  return '';
}

function getFieldPath(promotionType: PricingModel): FieldPath<FixedProductInput> | undefined {
  if (promotionType === PRICING_MODELS.FixedDeduction) {
    return 'variants.0.pricing.priceConfiguration.amountToDeduct';
  }

  if (promotionType === PRICING_MODELS.PercentageDeduction) {
    return 'variants.0.pricing.priceConfiguration.percentageToDeduct';
  }

  return undefined;
}

function getAmountPrefix(promotionType: PricingModel, currency: Currency) {
  if (promotionType === PRICING_MODELS.FixedDeduction) {
    return getCurrencySymbol(currency);
  }

  if (promotionType === PRICING_MODELS.PercentageDeduction) {
    return '%';
  }

  return '';
}
